import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import SmoothScroll from "@/components/SmoothScroll";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Watermark from "@/components/Watermark";
import Logo from "@/components/Logo";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Drops from "@/pages/Drops";
import Story from "@/pages/Story";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ComingSoon from "@/pages/ComingSoon";
import TrackOrder from "@/pages/TrackOrder";
import FAQ from "@/pages/FAQ";
import ShippingPolicy from "@/pages/ShippingPolicy";
import Returns from "@/pages/Returns";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Contact from "@/pages/Contact";
import ChatAssistant from "@/components/ChatAssistant";
import AdminApp from "@/admin/AdminApp";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function HomeWatermark() {
  const { pathname } = useLocation();
  return pathname === "/" ? <Watermark /> : null;
}

function PageTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    let vid = localStorage.getItem("sync_vid");
    if (!vid) {
      vid = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem("sync_vid", vid);
    }
    api.post("/track", { visitor_id: vid, path: pathname, referrer: document.referrer || null }).catch(() => {});
  }, [pathname]);
  return null;
}

function RestrictedShellPage({ children }) {
  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-ink/70 border-b border-border/60">
        <div className="container-sync flex items-center justify-between h-20">
          <Link to="/" data-testid="restricted-logo-link"><Logo variant="full" size="md" asLink={false} /></Link>
          <Link to="/" data-testid="restricted-back-link"
            className="font-mono text-xs uppercase tracking-[0.2em] text-chalk/80 hover:text-pulse link-underline">← Back</Link>
        </div>
      </header>
      {children}
    </>
  );
}

function RestrictedStory() {
  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-ink/70 border-b border-border/60">
        <div className="container-sync flex items-center justify-between h-20">
          <Link to="/" data-testid="restricted-logo-link"><Logo variant="full" size="md" asLink={false} /></Link>
          <Link to="/" data-testid="restricted-back-link"
            className="font-mono text-xs uppercase tracking-[0.2em] text-chalk/80 hover:text-pulse link-underline">← Back</Link>
        </div>
      </header>
      <Story />
    </>
  );
}

function SiteRouter() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [site, setSite] = useState(null);

  useEffect(() => {
    api.get("/site/status").then((r) => setSite(r.data)).catch(() => setSite({ coming_soon: true }));
  }, []);

  if (pathname.startsWith("/admin")) {
    return <AdminApp />;
  }

  if (site === null || (site.coming_soon && user === null)) {
    return <div className="min-h-screen bg-ink" />;
  }

  const restricted = site.coming_soon && (!user || user.role !== "admin");

  if (restricted) {
    return (
      <>
        <ScrollTop />
        <PageTracker />
        <main className="relative z-[2]">
          <Routes>
            <Route path="/" element={<ComingSoon />} />
            <Route path="/story" element={<RestrictedStory />} />
            <Route path="/track-order" element={<RestrictedShellPage><TrackOrder /></RestrictedShellPage>} />
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </>
    );
  }

  return (
    <>
      <ScrollTop />
      <PageTracker />
      <Navbar />
      <HomeWatermark />
      <CartDrawer />
      {site.coming_soon && (
        <div data-testid="coming-soon-banner"
          className="fixed bottom-0 inset-x-0 z-[70] bg-signal text-ink font-mono text-[11px] uppercase tracking-[0.2em] text-center py-2">
          Coming Soon mode — visitors only see the launch page & story
        </div>
      )}
      <main className="relative z-[2]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/drops" element={<Drops />} />
          <Route path="/story" element={<Story />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account" element={<Account />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
        </Routes>
      </main>
      <Footer />
      <ChatAssistant />
    </>
  );
}

function App() {
  return (
    <div className="App grain bg-ink text-chalk">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <SmoothScroll>
              <SiteRouter />
              <Toaster theme="dark" position="top-center" toastOptions={{
                style: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))", borderRadius: 0, fontFamily: "Space Mono, monospace", fontSize: "12px" },
              }} />
            </SmoothScroll>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
